import { useState, type ReactNode } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { EDUCATION_TYPE_LABELS, formatEducationPeriod } from '@hygge/shared';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { QueryError } from '@/components/ui/error-boundary';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { extractErrorMessage } from '@/lib/errors';
import { useDeleteEducation, useEducation, type Education } from '@/lib/employee-finance';
import { IconActionButton } from './icon-action-button';
import { EducationDialog } from './education-dialog';

interface EducationBlockProps {
  employeeId: string;
  /** `employees.profile.edit` — what POST / PUT / DELETE require. */
  canEdit: boolean;
}

function BlockCard({ actions, children }: { actions?: ReactNode; children: ReactNode }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border/50 bg-muted/30 px-5 py-3">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Education
        </h3>
        {actions}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

/**
 * The employee's education entries on the Documents tab [HW-1008], in the
 * order the server returns them: newest first, still studying on top.
 */
export function EducationBlock({ employeeId, canEdit }: EducationBlockProps) {
  const { data: entries, isLoading, error } = useEducation(employeeId);
  const remove = useDeleteEducation(employeeId);
  // `entry` outlives `open`: a dialog closing keeps showing what it was about
  // (Edit vs Add title, the institution in the delete question) until its
  // closing animation ends, instead of flipping to the empty case.
  // entry null = adding.
  const [editor, setEditor] = useState<{ open: boolean; entry: Education | null }>({
    open: false,
    entry: null,
  });
  const [deletion, setDeletion] = useState<{ open: boolean; entry: Education | null }>({
    open: false,
    entry: null,
  });
  const openEditor = (entry: Education | null) => setEditor({ open: true, entry });
  const closeDeletion = () => setDeletion((d) => ({ ...d, open: false }));

  const addButton = canEdit && (
    <Button
      variant="ghost"
      size="sm"
      className="h-7 gap-1.5 text-xs text-muted-foreground hover:text-foreground cursor-pointer"
      onClick={() => openEditor(null)}
    >
      <Plus className="h-3 w-3" />
      Add
    </Button>
  );

  const handleDelete = () => {
    if (!deletion.entry) return;
    remove.mutate(deletion.entry.id, {
      onSuccess: () => {
        toast.success('Education entry deleted');
        closeDeletion();
      },
      onError: (err) => toast.error(extractErrorMessage(err, 'Failed to delete education entry')),
    });
  };

  let body: ReactNode;
  if (isLoading) {
    body = (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  } else if (error) {
    body = <QueryError error={error} />;
  } else if (!entries?.length) {
    body = (
      <div className="flex flex-col items-start gap-3">
        <p className="text-sm text-muted-foreground">No education added yet.</p>
        {canEdit && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 cursor-pointer"
            onClick={() => openEditor(null)}
          >
            <Plus className="h-3.5 w-3.5" />
            Add education
          </Button>
        )}
      </div>
    );
  } else {
    body = (
      <ul className="divide-y divide-border/60">
        {entries.map((entry) => {
          const period = formatEducationPeriod(entry.startDate, entry.endDate);
          return (
            <li key={entry.id} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">
                  {EDUCATION_TYPE_LABELS[entry.type] ?? entry.type}
                </p>
                <p className="text-sm font-medium wrap-anywhere">{entry.institution}</p>
                {entry.specialty && (
                  <p className="text-sm text-muted-foreground wrap-anywhere">{entry.specialty}</p>
                )}
                {period && <p className="mt-0.5 text-xs text-muted-foreground">{period}</p>}
              </div>
              {canEdit && (
                <div className="flex shrink-0 items-center gap-1.5">
                  <IconActionButton
                    icon={Pencil}
                    tooltip={`Edit education: ${entry.institution}`}
                    size="sm"
                    onClick={() => openEditor(entry)}
                  />
                  <IconActionButton
                    icon={Trash2}
                    tooltip={`Delete education: ${entry.institution}`}
                    size="sm"
                    variant="destructive"
                    onClick={() => setDeletion({ open: true, entry })}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <>
      <BlockCard actions={addButton}>{body}</BlockCard>

      {canEdit && (
        <>
          <EducationDialog
            open={editor.open}
            onOpenChange={(next) => setEditor((e) => ({ ...e, open: next }))}
            employeeId={employeeId}
            entry={editor.entry}
          />
          <ConfirmDialog
            open={deletion.open}
            title={<>Delete &ldquo;{deletion.entry?.institution ?? ''}&rdquo;?</>}
            description="This action cannot be undone."
            confirmLabel="Delete"
            destructive
            isPending={remove.isPending}
            onOpenChange={(next) => {
              if (!next) closeDeletion();
            }}
            onConfirm={handleDelete}
          />
        </>
      )}
    </>
  );
}
